'use client';

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, CircleCheck, Info, Loader2, LogOut, Mail, MailCheck, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/use-session';
import { useVerificationStatus } from '@/hooks/use-verification-status';
import { Alert, AlertDescription, AlertIcon } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const RESEND_COOLDOWN_MS = 60_000;

export function EmailVerificationDialog() {
  const { t } = useTranslation();
  const { data: session, status } = useSession();
  const queryClient = useQueryClient();
  const { mutateAsync: checkVerificationStatus } = useVerificationStatus();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<'sent' | 'rateLimited' | 'failed' | 'notVerified' | null>(null);
  const [verified, setVerified] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const openedAt = useRef<number | null>(null);
  const inFlight = useRef(false);
  const lastAutomaticCheck = useRef<number | null>(null);
  useEffect(() => { setVerified(false); }, [session?.user?.sub]);
  useEffect(() => {
    if (cooldownUntil === null) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= cooldownUntil) setCooldownUntil(null);
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);
  const needsVerification = status === 'authenticated' && Boolean(session?.user?.termsAcceptedAt) &&
    session?.user?.emailVerified === false && !verified;
  const cooldownSeconds = cooldownUntil === null ? 0 : Math.max(0, Math.ceil((cooldownUntil - now) / 1_000));
  const tp = (key: string) => t(`auth.emailVerification.${key}`);

  async function checkStatus(showUnverified: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    if (showUnverified) setChecking(true);
    try {
      const data = await checkVerificationStatus();
      if (data.emailVerified) {
        setVerified(true);
        toast.success(tp('success'));
        await queryClient.invalidateQueries({ queryKey: ['session-me'] });
      } else if (showUnverified) {
        setResult('notVerified');
      }
    } catch {
      if (showUnverified) setResult('failed');
    } finally {
      inFlight.current = false;
      setChecking(false);
    }
  }

  useEffect(() => {
    if (!needsVerification) {
      openedAt.current = null;
      lastAutomaticCheck.current = null;
      return;
    }
    if (openedAt.current === null) openedAt.current = Date.now();
    const automaticCheck = () => {
      const current = Date.now();
      if (document.visibilityState === 'visible' && openedAt.current !== null &&
          current - openedAt.current < 10 * 60 * 1000 && !inFlight.current &&
          (lastAutomaticCheck.current === null || current - lastAutomaticCheck.current >= 5_000)) {
        lastAutomaticCheck.current = current;
        void checkStatus(false);
      }
    };
    const timer = window.setInterval(automaticCheck, 30_000);
    document.addEventListener('visibilitychange', automaticCheck);
    window.addEventListener('focus', automaticCheck);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', automaticCheck);
      window.removeEventListener('focus', automaticCheck);
    };
    // The timer is scoped to the modal opening; checkStatus reads only stable query client and translations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsVerification]);

  async function handleResend() {
    if (sending || cooldownSeconds > 0) return;
    setSending(true);
    try {
      const response = await fetch('/api/auth/resend-verification', { method: 'POST' });
      if (response.ok) {
        const data: unknown = await response.json();
        if (typeof data === 'object' && data !== null && 'alreadyVerified' in data && data.alreadyVerified === true) {
          await checkStatus(true);
        } else {
          setResult('sent');
          const sentAt = Date.now();
          setNow(sentAt);
          setCooldownUntil(sentAt + RESEND_COOLDOWN_MS);
        }
      } else {
        setResult(response.status === 429 ? 'rateLimited' : 'failed');
      }
    } catch { setResult('failed'); }
    finally { setSending(false); }
  }

  if (!needsVerification) return null;

  const resultVariant = result === 'sent' ? 'success' : result === 'failed' ? 'destructive' : 'info';
  const ResultIcon = result === 'sent' ? CircleCheck : result === 'failed' ? CircleAlert : Info;

  return (
    <Dialog open modal>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md gap-3 rounded-xl bg-card p-6 shadow-lg sm:p-8"
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        data-nosnippet
      >
        <DialogHeader className="mb-0 items-center space-y-0 gap-3 text-center sm:text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary/10">
            <MailCheck className="size-7 text-primary" aria-hidden="true" />
          </div>
          <DialogTitle className="text-xl font-semibold">{tp('title')}</DialogTitle>
          <DialogDescription>{tp('subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="flex min-w-0 justify-center">
          <span className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm font-medium">
            <Mail className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 max-w-full truncate whitespace-nowrap" title={session?.user?.email ?? undefined}>
              {session?.user?.email}
            </span>
          </span>
        </div>
        <p className="text-center text-xs leading-relaxed text-muted-foreground">
          {tp('hint')}
        </p>
        {result && (
          <Alert variant={resultVariant} appearance="light" role={result === 'failed' ? 'alert' : 'status'}>
            <AlertIcon><ResultIcon className="size-5" aria-hidden="true" /></AlertIcon>
            <AlertDescription>
              {tp(result)}
              {result === 'sent' && <span className="mt-1 block">{tp('checkSpam')}</span>}
            </AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
          <Button className="h-10 w-full rounded-lg" onClick={() => void checkStatus(true)} disabled={checking}>
            {checking ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Check className="size-4" aria-hidden="true" />}
            {checking ? tp('checking') : tp('confirm')}
          </Button>
          <Button
            variant="outline"
            className="h-10 w-full rounded-lg"
            onClick={handleResend}
            disabled={sending || cooldownSeconds > 0}
          >
            {sending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RotateCw className="size-4" aria-hidden="true" />}
            {sending ? tp('sending') : cooldownSeconds > 0
              ? t('auth.emailVerification.resendCooldown', { seconds: cooldownSeconds }) : tp('resend')}
          </Button>
        </div>
        <div className="mt-2 flex justify-center border-t border-border pt-3">
          <Button variant="ghost" size="sm" asChild className="h-10 text-xs text-muted-foreground hover:text-foreground">
            <a href="/auth/logout"><LogOut className="size-3.5" aria-hidden="true" />{tp('logout')}</a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
